import { useState, useRef, TouchEvent, useEffect } from 'react';

interface Product {
  id: number;
  name: string;
  weight: string;
  image: string;
  display_order: number;
}

export default function ProductsSection() {
  const [products, setProducts] = useState<Product[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideOffset, setSlideOffset] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const autoScrollTimer = useRef<number | null>(null);
  const lastInteractionTime = useRef<number>(Date.now());
  const [isMobile, setIsMobile] = useState(false);

  const titleSize = '3.5vw';
  const titleSizeMobile = '10vw';

  useEffect(() => {
    fetchProducts();
    
    // Detect if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-scroll effect - ONLY for mobile
  useEffect(() => {
    if (!isMobile) return; // Skip auto-scroll on desktop

    const checkAutoScroll = () => {
      const timeSinceLastInteraction = Date.now() - lastInteractionTime.current;
      
      if (timeSinceLastInteraction >= 4000 && products.length > 0 && !isTransitioning) {
        nextSlide();
        lastInteractionTime.current = Date.now();
      }
    };

    autoScrollTimer.current = window.setInterval(checkAutoScroll, 1000);

    return () => {
      if (autoScrollTimer.current) {
        window.clearInterval(autoScrollTimer.current);
      }
    };
  }, [products.length, isTransitioning, currentSlide, isMobile]);

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://mulata.pt/bolacha-mulata/api/products.php');
      const result = await response.json();
      if (result && Array.isArray(result)) {
        setProducts(result);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const getImageSrc = (path: string) => {
    if (path.startsWith('http')) {
      return path;
    }
    return path;
  };

  const nextSlide = () => {
    if (isTransitioning || products.length === 0) return;
    setIsTransitioning(true);
    
    setSlideOffset(prev => prev - 480);
    
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
      setSlideOffset(0);
      setIsTransitioning(false);
    }, 400);
  };

  const prevSlide = () => {
    if (isTransitioning || products.length === 0) return;
    setIsTransitioning(true);
    
    setSlideOffset(prev => prev + 480);
    
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
      setSlideOffset(0);
      setIsTransitioning(false);
    }, 400);
  };

  const getVisibleProducts = () => {
    if (products.length === 0) return [];
    
    const visible = [];
    for (let i = -2; i <= 2; i++) {
      const index = (currentSlide + i + products.length) % products.length;
      visible.push({
        product: products[index],
        position: i,
        key: `${products[index].id}-${i}`
      });
    }
    return visible;
  };

  const handleProductTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    lastInteractionTime.current = Date.now();
  };

  const handleProductTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
    lastInteractionTime.current = Date.now();
  };

  const handleProductTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    lastInteractionTime.current = Date.now();
  };

  const handleDotClick = (index: number) => {
    if (index !== currentSlide && !isTransitioning) {
      if (index > currentSlide) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    lastInteractionTime.current = Date.now();
  };

  if (products.length === 0) {
    return (
      <section 
        className="products-section-desktop relative" 
        style={{ 
          backgroundImage: 'url(https://i.imgur.com/wwRF6Ht.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          paddingBottom: '57.29%',
          position: 'relative',
          height: 0,
          imageRendering: '-webkit-optimize-contrast',
          overflow: 'visible'
        }}
      >
        <div 
          className="absolute inset-0 flex flex-col items-center justify-end"
          style={{
            paddingBottom: '2.08vw'
          }}
        >
          <h2 
            className="text-center prompt-black-font" 
            style={{ 
              color: '#ffffff', 
              letterSpacing: '0.05em',
              fontSize: titleSize,
              marginBottom: '2.08vw'
            }}
          >
            PRODUTOS
          </h2>
          <p className="text-white" style={{ fontSize: '1.25vw' }}>Carregando produtos...</p>
        </div>
      </section>
    );
  }

  const visibleProducts = getVisibleProducts();

  return (
    <>
      {/* Products Section - Desktop */}
      <section 
        className="products-section-desktop relative" 
        style={{ 
          backgroundImage: 'url(https://i.imgur.com/wwRF6Ht.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          paddingBottom: '57.29%',
          position: 'relative',
          height: 0,
          imageRendering: '-webkit-optimize-contrast',
          overflow: 'visible',
          zIndex: 30
        }}
      >
        <div 
          className="absolute left-0 right-0 flex flex-col items-center"
          style={{
            top: '51%',
            zIndex: 40
          }}
        >
          <h2 
            className="text-center prompt-black-font" 
            style={{ 
              color: '#ffffff', 
              letterSpacing: '0.05em',
              fontSize: titleSize,
              marginBottom: '1vw'
            }}
          >
            PRODUTOS
          </h2>

          <div className="relative w-full flex flex-col items-center">
            <div 
              className="relative w-full" 
              style={{ height: '28.65vw' }}
            >
              <div className="flex items-center justify-center gap-8 h-full">
                {visibleProducts.map(({ product, position, key }) => {
                  const baseTranslateX = position * 25;
                  const totalTranslateX = baseTranslateX + (slideOffset / 19.2);
                  const isCenter = position === 0;
                  
                  return (
                    <div
                      key={key}
                      className="absolute flex flex-col items-center"
                      style={{
                        width: '23.44vw',
                        left: '50%',
                        transform: `translateX(calc(-50% + ${totalTranslateX}vw))`,
                        opacity: 1,
                        transition: slideOffset !== 0 
                          ? 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' 
                          : 'transform 0.3s ease',
                        pointerEvents: 'auto',
                        zIndex: position === 0 ? 10 : 5 - Math.abs(position)
                      }}
                    >
                      <div 
                        className="flex items-center justify-center" 
                        style={{ 
                          height: '27vw',
                          width: '100%'
                        }}
                      >
                        <img
                          src={getImageSrc(product.image)}
                          alt={product.name}
                          className="product-image-tilt-small"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            animation: isCenter ? 'productPulse 0.8s ease-in-out' : 'none'
                          }}
                          onError={(e) => {
                            e.currentTarget.src = '/Imagens/placeholder.png';
                          }}
                        />
                      </div>
                      <h3 
                        className="prompt-font text-white text-center"
                        style={{
                          fontSize: '2.08vw',
                          marginTop: '0vw'
                        }}
                      >
                        {product.name}
                      </h3>
                      <p 
                        className="rethink-bold-font text-white text-center"
                        style={{
                          fontSize: '2.08vw',
                          marginTop: '0.2vw'
                        }}
                      >
                        {product.weight}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div 
              className="flex justify-center"
              style={{
                gap: '1.25vw',
                marginTop: '3.67vw',
                position: 'relative',
                zIndex: 50
              }}
            >
              <button
                onClick={prevSlide}
                className="nav-button"
                aria-label="Anterior"
                disabled={isTransitioning}
                style={{
                  width: '3.33vw',
                  height: '3.33vw'
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <button
                onClick={nextSlide}
                className="nav-button"
                aria-label="Próximo"
                disabled={isTransitioning}
                style={{
                  width: '3.33vw',
                  height: '3.33vw'
                }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section - Mobile - 600x730px (121.67% ratio) */}
      <section 
        className="products-section-mobile relative" 
        style={{ 
          backgroundImage: 'url(https://i.imgur.com/haCFry0.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          width: '100%',
          paddingBottom: '121.67%',
          position: 'relative',
          height: 0,
          imageRendering: '-webkit-optimize-contrast',
          overflow: 'visible',
          zIndex: 40,
          marginBottom: '-1px'
        }}
      >
        <div 
          className="absolute left-0 right-0 w-full px-4 flex flex-col items-center"
          style={{
            top: '37%',
            zIndex: 50
          }}
        >
          <h2 
            className="text-center prompt-black-font" 
            style={{ 
              color: '#ffffff', 
              letterSpacing: '0.05em',
              fontSize: titleSizeMobile,
              fontWeight: 900,
              marginBottom: '10vw'
            }}
          >
            PRODUTOS
          </h2>

          <div 
            className="relative w-full flex flex-col items-center touch-pan-y"
            onTouchStart={handleProductTouchStart}
            onTouchMove={handleProductTouchMove}
            onTouchEnd={handleProductTouchEnd}
          >
            <div 
              className="relative w-full" 
              style={{ height: '70vw', overflow: 'visible' }}
            >
              <div className="flex items-center justify-center h-full">
                {visibleProducts.map(({ product, position, key }) => {
                  const baseTranslateX = position * 90;
                  const totalTranslateX = baseTranslateX + (slideOffset / 5.33);
                  const isCenter = position === 0;
                  const isSide = Math.abs(position) === 1;
                  
                  return (
                    <div
                      key={key}
                      className="absolute flex flex-col items-center"
                      style={{
                        width: '75vw',
                        left: '50%',
                        transform: `translateX(calc(-50% + ${totalTranslateX}vw))`,
                        opacity: isCenter || isSide ? 1 : 0,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        pointerEvents: isCenter ? 'auto' : 'none',
                        zIndex: isCenter ? 10 : 5 - Math.abs(position)
                      }}
                    >
                      <div 
                        className="flex items-center justify-center product-image-container-no-zoom" 
                        style={{ 
                          height: '60vw',
                          width: '100%'
                        }}
                      >
                        <img
                          src={getImageSrc(product.image)}
                          alt={product.name}
                          className="max-w-full max-h-full object-contain product-image-tilt-small"
                          style={{
                            maxHeight: '60vw'
                          }}
                          onError={(e) => {
                            e.currentTarget.src = '/Imagens/placeholder.png';
                          }}
                        />
                      </div>
                      <h3 
                        className="prompt-font text-white text-center"
                        style={{
                          fontSize: '6vw',
                          marginTop: '0vw'
                        }}
                      >
                        {product.name}
                      </h3>
                      <p 
                        className="rethink-bold-font text-white text-center"
                        style={{
                          fontSize: '5vw',
                          marginTop: '-2vw',
                          marginBottom: '10vw'
                        }}
                      >
                        {product.weight}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div 
              className="flex justify-center"
              style={{
                gap: '3vw',
                marginTop: '0vw'
              }}
            >
              {products.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className="transition-all duration-300"
                >
                  <div
                    style={{
                      width: '3vw',
                      height: '3vw',
                      borderRadius: '50%',
                      backgroundColor: index === currentSlide ? '#399cff' : 'rgb(255, 255, 255)'
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
